/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps } from '@mui/material';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  styled,
} from '@mui/material';
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import React from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import type { AirmetFromBackend, SigmetFromBackend } from '@opengeoweb/api';
import {
  defaultFormOptions,
  ReactHookFormHiddenInput,
  ReactHookFormProvider,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  CustomTooltip,
  dateUtils,
  useConfirmationDialog,
} from '@opengeoweb/shared';
import type {
  Airmet,
  AirmetConfig,
  AviationProduct,
  FormMode,
  ProductConfig,
  ProductStatus,
  Sigmet,
  SigmetConfig,
} from '@opengeoweb/sigmet-airmet';
import {
  AirmetForm,
  CancelSigmetVolcanicMovements,
  getConfirmationDialogButtonLabel,
  getConfirmationDialogCancelLabel,
  getConfirmationDialogContent,
  getConfirmationDialogTitle,
  isInstanceOfCancelAirmet,
  isInstanceOfCancelSigmet,
  isVAPhenomenon,
  ProductAction,
  SigmetForm,
  useShortTestHelpers,
} from '@opengeoweb/sigmet-airmet';
import type { MapPreset } from '@opengeoweb/store';
import { breakpoints, Delete, DrawPolygon } from '@opengeoweb/theme';
import {
  DEFAULT_GEOJSON_STYLE_FILL_OPACITY,
  FEATURE_DRAW_FILL_COLOR,
  FEATURE_DRAW_SECONDARY_FILL_COLOR,
  FEATURE_DRAW_STROKE_COLOR,
} from '@opengeoweb/webmap-react';
import { WarningsMapView } from '../WarningsMapView/WarningsMapView';
import { AreaField } from './AreaField/AreaField';
import { DescriptionField } from './DescriptionField';
import type { ColorDefinition, WarningLevel } from './LevelField';
import { LevelColors, LevelField } from './LevelField';
import { Phenomenon } from './PhenomenonField';
import { ProbabilityField } from './ProbabilityField';
import { ValidFrom } from './ValidFromField';
import { ValidUntil } from './ValidUntilField';
import { useProductConfig, useTACApi } from '../../api/aviation-api/hooks';
import type {
  BaseDrawingListItem,
  DrawingListItem,
} from '../../store/warningsDrawings/types';
import type { FormError } from '../../store/warningsForm/reducer';
import type {
  Area,
  PublicWarningDetail,
  WarningType,
} from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import {
  getEmptyAirmetWarning,
  getEmptySigmetWarning,
} from '../../utils/sig-airmet-util';
import { useFormDirty } from '../../utils/useFormDirty';
import { MaritimeWarningsFormConnect } from '../MaritimeWarningsForm/MaritimeWarningsFormConnect';

export const useScrollTopOnError = (
  error: FormError,
): React.RefObject<HTMLDivElement | null> => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (error?.message && ref.current) {
      ref.current.scrollTop = 0;
    }
  }, [error]);
  return ref;
};

export const getEmptyPublicWarning = (): PublicWarningDetail => {
  const currentTime = new Date();
  const validFromTime = dateUtils.add(currentTime, { minutes: 15 });
  const validUntilTime = dateUtils.add(currentTime, { hours: 6, minutes: 15 });

  return {
    id: '',
    phenomenon: '',
    validFrom: dateUtils.dateToString(validFromTime)!,
    validUntil: dateUtils.dateToString(validUntilTime)!,
    level: '',
    probability: 30,
    descriptionOriginal: '',
    descriptionTranslation: '',
    areas: [],
  };
};

export const getFormData = (
  publicWarningsDetails: PublicWarningDetail | undefined,
  object?: DrawingListItem,
): FormData => {
  const data = {
    IS_DRAFT: '',
    'translation-select': '',
    ...(publicWarningsDetails
      ? {
          ...getEmptyPublicWarning(),
          ...publicWarningsDetails,
        }
      : getEmptyPublicWarning()),
    object,
  };

  return data;
};

// tablet breakpoint - 2 padding * 2 for both sides
const containerQueryBreakpoint = `@container (min-width: ${
  breakpoints.tablet - 32
}px)`;
const spinnerSx: SxProps = {
  width: '16px!important',
  height: '16px!important',
  position: 'absolute',
  top: 0,
  bottom: 0,
  margin: 'auto',
  marginLeft: 1,
};

const ContainerWrapper = styled('div')(() => ({
  containerType: 'size',
  height: '100%',
}));

const ContainerFormWrapper = styled('div')<{
  isReadOnly: boolean;
  addPadding?: boolean;
}>(({ isReadOnly = false, addPadding = true, theme }) => ({
  padding: addPadding ? theme.spacing(2) : 0,
  height: isReadOnly ? '100%' : `calc(100% - 206px)`,
  overflowY: 'auto',
  [containerQueryBreakpoint]: {
    height: isReadOnly ? '100%' : 'calc(100% - 88px)',
  },
}));

const sm6Width =
  'calc(100% * 6 / var(--Grid-columns) - (var(--Grid-columns) - 6) * (var(--Grid-columnSpacing) / var(--Grid-columns)))';
const sm4Width =
  'calc(100% * 4 / var(--Grid-columns) - (var(--Grid-columns) - 4) * (var(--Grid-columnSpacing) / var(--Grid-columns)))';
const ContainerGrid = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '.MuiGrid-grid-sm-6, .MuiGrid-grid-sm-4': {
    width: '100%',
    minWidth: '100%',
  },
  [containerQueryBreakpoint]: {
    '.MuiGrid-grid-sm-6': {
      width: sm6Width,
      minWidth: sm6Width,
    },
    '.MuiGrid-grid-sm-4': {
      width: sm4Width,
      minWidth: sm4Width,
    },
  },
}));

interface FormData extends PublicWarningDetail {
  IS_DRAFT?: boolean | string;
  object?: BaseDrawingListItem;
  'translation-select'?: string;
}

export const prepareFormValues = (data: FormData): PublicWarningDetail => {
  // remove unneeded fields
  const {
    IS_DRAFT,
    object,
    'translation-select': translationSelect,
    ...productToPost
  } = data;
  // remove empty fields
  return Object.keys(productToPost).reduce<PublicWarningDetail>(
    (warningDetail: PublicWarningDetail, fieldName: string) => {
      const key = fieldName as keyof PublicWarningDetail;
      const fieldValue = productToPost[key];
      if (Array.isArray(fieldValue)) {
        if (!fieldValue.length || fieldValue[0].geoJSON === null) {
          return warningDetail;
        }
      }

      if (fieldValue) {
        return {
          ...warningDetail,
          [key]: fieldValue,
        };
      }

      return warningDetail;
    },
    {} as PublicWarningDetail,
  );
};

export interface FormProps {
  defaultMapPreset?: MapPreset;
  onSaveForm?: (formValues: PublicWarningDetail) => Promise<Headers | null>;
  onPublishForm?: (formValues: PublicWarningDetail) => Promise<Headers | null>;
  onClearForm?: () => void;
  onCloseForm?: () => void;
  onSaveAviationForm?: (
    productToPost: AviationProduct,
    newStatus: ProductStatus,
    warningType: 'sigmet' | 'airmet',
    vaSigmetMoveToFIR?: string,
    setSelectedUUID?: (uuid: string) => void,
  ) => Promise<SigmetFromBackend | AirmetFromBackend | undefined>;
  object?: DrawingListItem;
  publicWarningDetails?: PublicWarningDetail;
  error?: FormError;
  isReadOnly?: boolean;
  onFormDirty?: (isFormDirty: boolean) => void;
  isSnapshot?: boolean;
  onAddObject?: () => void;
  selectedAviationProduct?: AviationProduct;
  warningType?: WarningType;
  mode?: FormMode;
  formState?: string;
  onSetViewModeResetArea?: (area: Area[]) => void;
  storedStartGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
  storedEndGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
  setWarningLevel?: (level: WarningLevel | '') => void;
  linkedMapIds?: string[];
  onDrawPolygon?: () => void;
  onDeletePolygon?: () => void;
  isDrawPolygonActive?: boolean;
  isDrawingModeActive?: boolean;
  publicWarningStatus?: PublicWarningStatus;
}

interface FormErrors {
  title: string;
  errors: React.ReactElement[];
}

const getErrors = (errors: string): null | FormErrors =>
  errors
    ? errors?.split(/\r?\n/).reduce<FormErrors>(
        (allErrors, error, index) => {
          if (index === 0) {
            return {
              ...allErrors,
              title: error,
            };
          }
          const isEven = index % 2 === 0;

          return {
            ...allErrors,
            errors: allErrors.errors.concat(
              // eslint-disable-next-line react/no-array-index-key
              <p key={`error-${index}`} style={{ fontSize: 14 }}>
                {isEven ? error : <i>{error}</i>}
              </p>,
            ),
          };
        },
        { title: '', errors: [] },
      )
    : null;

export function warningLevelFeatureColors(
  level: WarningLevel | undefined,
  geoJSON: FeatureCollection<Geometry, GeoJsonProperties> | undefined, // Allow undefined
  isAviationWarning = false, // Determines if it's an aviation warning
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (!geoJSON?.features) {
    // Return an empty FeatureCollection if geoJSON is invalid
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  return {
    ...geoJSON,
    features: geoJSON.features.map((feature) => {
      let colors: ColorDefinition;

      if (isAviationWarning) {
        const isStartGeometry = feature.properties?.isStartGeometry ?? false;
        colors = {
          color: isStartGeometry
            ? FEATURE_DRAW_FILL_COLOR
            : FEATURE_DRAW_SECONDARY_FILL_COLOR, // Orange for start, purple for end
          borderColor: isStartGeometry
            ? FEATURE_DRAW_FILL_COLOR
            : FEATURE_DRAW_SECONDARY_FILL_COLOR,
        };
      } else if (level && level in LevelColors) {
        // Public warning: use LevelColors if level is defined
        colors = LevelColors[level];
      } else {
        // Public warning: default to blue if level is undefined=
        colors = {
          color: FEATURE_DRAW_FILL_COLOR,
          borderColor: FEATURE_DRAW_STROKE_COLOR,
        };
      }

      const defaultProperties = {
        fill: colors.color,
        stroke: colors.color,
        'fill-opacity': DEFAULT_GEOJSON_STYLE_FILL_OPACITY,
        'stroke-width': 2,
        'stroke-opacity': 1,
      };

      return {
        ...feature,
        properties: {
          ...defaultProperties,
          ...feature.properties,
          fill: colors.color || feature.properties?.fill,
          stroke: colors.color || feature.properties?.stroke,
        },
      };
    }),
  };
}

const Form: React.FC<FormProps> = ({
  defaultMapPreset,
  onSaveForm = async (): Promise<null> => null,
  onPublishForm = async (): Promise<null> => null,
  onClearForm = (): void => {},
  onCloseForm = (): void => {},
  onSaveAviationForm = async (): Promise<null> => null,
  object,
  publicWarningDetails,
  error = { severity: 'error', message: '' },
  isReadOnly = false,
  onFormDirty = (): void => {},
  isSnapshot = false,
  onAddObject = (): void => {},
  selectedAviationProduct,
  warningType = 'public',
  mode,
  formState,
  onSetViewModeResetArea = (): void => {},
  storedStartGeoJSON,
  storedEndGeoJSON,
  setWarningLevel = (): void => {},
  linkedMapIds = [],
  onDrawPolygon = (): void => {},
  onDeletePolygon = (): void => {},
  isDrawPolygonActive = false,
  isDrawingModeActive = false,
  publicWarningStatus,
}) => {
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const isSavingAviationDraftRef = React.useRef(false);
  const { fetchSigmetTAC, fetchAirmetTAC } = useTACApi();
  const confirmationDialog = useConfirmationDialog();
  const formMethods = useForm<Record<string, unknown>>({ defaultValues: {} });
  const { isShortTest } = useShortTestHelpers();

  const endOfWarningValidity = publicWarningDetails?.validUntil;
  React.useEffect(() => {
    if (!endOfWarningValidity || formState !== 'amending') {
      return (): void => {};
    }

    const end = new Date(endOfWarningValidity).getTime();
    const now = Date.now();

    if (end <= now) {
      onCloseForm();
      return (): void => {};
    }

    const timer = setTimeout(
      () => {
        onCloseForm();
      },
      end - now + 1000,
    );

    return (): void => clearTimeout(timer);
  }, [endOfWarningValidity, formState, onCloseForm]);

  const initialPublicWarningArea = publicWarningDetails?.areas?.length
    ? publicWarningDetails?.areas[0]?.geoJSON
    : undefined;

  const [warningGeoJSONFeature, setWarningGeoJSONFeature] = React.useState(
    initialPublicWarningArea,
  );

  const [originalWarningAreas, setOriginalWarningAreas] = React.useState(
    publicWarningDetails?.areas,
  );

  const { t } = useWarningsTranslation();
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
    getFieldState,
    getValues,
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const { toggleIsDraft, DraftFieldHelper } = useDraftFormHelpers();

  const areFieldsDisabled = isPublishing || isSaving;
  const isPublicWarningInDrawingMode =
    warningType === 'public' && isDrawingModeActive;

  useFormDirty({
    isDirty: Object.keys(dirtyFields).length > 0,
    isLoading: isPublishing || isSaving,
    error: error?.message,
    onFormDirty,
    onSuccess: () => {
      // reset form dirty and update defaultvalues and formvalues after successfull saving
      reset(getValues());
    },
  });

  const onPressPublish = (): void => {
    if (!isPublishing) {
      toggleIsDraft(false);
      void handleSubmit(async (data) => {
        setIsPublishing(true);
        await onPublishForm(
          prepareFormValues(data as PublicWarningDetail),
        ).catch(() => {});

        setIsPublishing(false);
      })();
    }
  };

  const onPressSave = (): void => {
    if (!isSaving) {
      toggleIsDraft(true);
      void handleSubmit(async (data) => {
        setIsSaving(true);
        await onSaveForm(prepareFormValues(data as PublicWarningDetail)).catch(
          () => {},
        );

        setIsSaving(false);
      })();
    }
  };

  const showConfirmDialog = async (action: ProductAction): Promise<void> =>
    confirmationDialog({
      title: getConfirmationDialogTitle(
        t,
        action,
        (warningType as 'sigmet') || 'airmet',
      ),
      description: getConfirmationDialogContent(
        t,
        action,
        (warningType as 'sigmet') || 'airmet',
      ),
      confirmLabel: getConfirmationDialogButtonLabel(
        t,
        action,
        (warningType as 'sigmet') || 'airmet',
      ),
      cancelLabel: getConfirmationDialogCancelLabel(t, action),
      catchOnCancel: action === ProductAction.CLOSE,
      content:
        isVAPhenomenon(selectedAviationProduct!, action) && !isShortTest() ? (
          <FormProvider {...formMethods}>
            <CancelSigmetVolcanicMovements
              productConfig={aviationConfig as SigmetConfig}
              sigmet={selectedAviationProduct as Sigmet}
            />
          </FormProvider>
        ) : null,
    });

  const cancelAviationProduct = async (
    warningType: 'sigmet' | 'airmet',
  ): Promise<SigmetFromBackend | AirmetFromBackend | null> => {
    const result = await onSaveAviationForm(
      selectedAviationProduct!,
      'CANCELLED',
      warningType,
      isVAPhenomenon(selectedAviationProduct!, ProductAction.CANCEL)
        ? (formMethods.getValues('vaSigmetMoveToFIR') as string | undefined)
        : undefined,
    );
    return result ?? null;
  };

  const getPublishButtonLabel = (): string => {
    if (isPublishing) {
      return t('warning-button-publishing');
    }

    if (selectedAviationProduct) {
      return t('cancel-warningType', { warningType });
    }

    return t('warning-button-publish');
  };

  const saveAviationDraft = async (
    formValues: AviationProduct,
    warningType: 'sigmet' | 'airmet',
  ): Promise<void> => {
    isSavingAviationDraftRef.current = true;
    await onSaveAviationForm(
      formValues,
      'DRAFT',
      warningType,
      undefined,
      (uuid) => {
        setValue('uuid', uuid, {
          shouldValidate: true,
          shouldDirty: false,
        });
      },
    ).finally(() => {
      isSavingAviationDraftRef.current = false;
    });
  };

  const handleSubmitAviationProduct = async (
    action: ProductAction,
    warningType: 'sigmet' | 'airmet',
  ): Promise<void> => {
    if (isSaving || isSavingAviationDraftRef.current) {
      return;
    }

    switch (action) {
      case ProductAction.SAVE: {
        toggleIsDraft(true);
        await trigger();

        const startGeometryErrorType =
          getFieldState('startGeometry').error?.type;
        const endGeometryErrorType = getFieldState('endGeometry').error?.type;
        const hasConfiguredIntersectionPointLimitError =
          startGeometryErrorType === 'hasConfiguredIntersectionPointLimit' ||
          endGeometryErrorType === 'hasConfiguredIntersectionPointLimit';

        if (hasConfiguredIntersectionPointLimitError) {
          return;
        }

        await saveAviationDraft(getValues() as AviationProduct, warningType);
        break;
      }
      case ProductAction.PUBLISH:
        toggleIsDraft(false);
        void handleSubmit(async (formValues) => {
          try {
            await showConfirmDialog(action);
          } catch {
            return;
          }

          setIsPublishing(true);
          try {
            await onSaveAviationForm(
              formValues as AviationProduct,
              'PUBLISHED',
              warningType,
            );
            onCloseForm();
          } catch {
            /* empty */
          } finally {
            setIsPublishing(false);
          }
        })();
        break;
      case ProductAction.CANCEL:
        try {
          await showConfirmDialog(action);
        } catch {
          return;
        }

        setIsPublishing(true);
        try {
          await cancelAviationProduct(warningType);
          onCloseForm();
        } catch {
          /* empty */
        } finally {
          setIsPublishing(false);
        }
        break;
      case ProductAction.AMEND:
        void handleSubmit(async (formValues) => {
          try {
            await showConfirmDialog(action);
            setIsPublishing(true);

            // 1) Publish amended warning
            await onSaveAviationForm(
              formValues as AviationProduct,
              'PUBLISHED',
              warningType,
            );

            // 2) Cancel the original
            await cancelAviationProduct(warningType);

            onCloseForm();
          } finally {
            setIsPublishing(false);
          }
        })();
        break;
      case ProductAction.RENEW:
      case ProductAction.DISCARD:
      case ProductAction.DUPLICATE:
      case ProductAction.CLOSE:
        break;

      default:
        console.warn(`Unhandled product action: ${action}`);
    }
  };

  const onPressClear = (): void => {
    switch (warningType) {
      case 'sigmet':
        reset(getEmptySigmetWarning((aviationConfig as ProductConfig) ?? {}));
        break;
      case 'airmet':
        reset(getEmptyAirmetWarning((aviationConfig as ProductConfig) ?? {}));
        break;
      case 'public':
      default:
        reset(getEmptyPublicWarning());
        setWarningGeoJSONFeature(undefined);
        setWarningLevel('');
        break;
    }

    onClearForm();
  };

  // When switching warning, reset the form to the new values
  const publicWarningId = publicWarningDetails?.id;
  React.useEffect(() => {
    if (selectedAviationProduct) {
      return;
    }
    // sync redux with react-hook-form
    reset(getFormData(publicWarningDetails, object));
    setOriginalWarningAreas(publicWarningDetails?.areas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicWarningId]);

  // When the editor gets set back to viewer and the form is dirty, reset any changes made
  React.useEffect(() => {
    if (selectedAviationProduct) {
      return;
    }

    if (
      isReadOnly &&
      (Object.keys(dirtyFields).length > 0 ||
        publicWarningDetails?.areas !== originalWarningAreas)
    ) {
      // // sync redux with react-hook-form
      const oldPublicWarning = {
        ...publicWarningDetails,
        areas: originalWarningAreas,
      } as PublicWarningDetail;
      reset(getFormData(oldPublicWarning, object));
      if (publicWarningDetails?.areas !== originalWarningAreas) {
        // update the store to contain the original area again
        onSetViewModeResetArea(originalWarningAreas || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  const areas = publicWarningDetails?.areas;

  React.useEffect(() => {
    if (selectedAviationProduct) {
      return;
    }
    if (areas) {
      // sync redux with react-hook-form
      setValue('areas', areas, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, setValue]);

  const level: WarningLevel | undefined = watch('level');
  const hasPublicWarningPolygon = Boolean(
    warningGeoJSONFeature?.features.length,
  );
  const isDrawPolygonDisabled =
    areFieldsDisabled || isReadOnly || linkedMapIds.length === 0;
  const shouldDisablePolygonDeleteForWarningStatus =
    publicWarningStatus !== undefined &&
    publicWarningStatus !== PublicWarningStatus.DRAFT &&
    hasPublicWarningPolygon;
  const isDeletePolygonDisabled =
    isDrawPolygonDisabled ||
    !hasPublicWarningPolygon ||
    shouldDisablePolygonDeleteForWarningStatus;
  const drawToolsDisabledTooltip =
    linkedMapIds.length === 0 ? t('warning-draw-tools-disabled') : undefined;
  let deletePolygonDisabledTooltip: string | undefined;
  if (linkedMapIds.length === 0) {
    deletePolygonDisabledTooltip = t('warning-draw-tools-disabled');
  } else if (shouldDisablePolygonDeleteForWarningStatus) {
    deletePolygonDisabledTooltip = t('disabled-button-tooltip');
  }

  React.useEffect(() => {
    if (selectedAviationProduct) {
      return;
    }
    // sync drawing on map with selected warning including selected level color
    const geoJSONFeature = warningLevelFeatureColors(
      level,
      initialPublicWarningArea,
    );
    setWarningGeoJSONFeature(geoJSONFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPublicWarningArea, level]);

  React.useEffect(() => {
    if (selectedAviationProduct) {
      return;
    }
    // update level in redux so linked map updates feature color accordingly
    setWarningLevel(level || '');
  }, [level, selectedAviationProduct, setWarningLevel]);

  const aviationId = selectedAviationProduct?.uuid;

  const publishedStart = publicWarningDetails?.validFrom;

  const nowIso = dateUtils.getCurrentTimeAsString();
  const lockValidFrom =
    formState === 'amending' &&
    !!publishedStart &&
    new Date(publishedStart).getTime() <= new Date(nowIso).getTime();

  React.useEffect(() => {
    if (!selectedAviationProduct) {
      return;
    }
    // sync redux with react-hook-form
    reset(selectedAviationProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aviationId, warningType]);

  const errors = getErrors(error.message);

  const ref = useScrollTopOnError(error);
  const { data: aviationConfig } = useProductConfig(warningType);

  const isMaritimeWarning =
    warningType === 'maritimeWarningCoastal' ||
    warningType === 'maritimeWarningNorthSea';
  const getFormContent = (): React.ReactElement => {
    // Aviation forms
    if (selectedAviationProduct) {
      if (warningType === 'sigmet') {
        return (
          <Box
            sx={{
              maxWidth: { xs: '550px', sm: '750px' },
              marginLeft: '-80px',
            }}
          >
            <SigmetForm
              getTAC={async (product) => fetchSigmetTAC(product)}
              initialCancelSigmet={
                selectedAviationProduct !== undefined &&
                isInstanceOfCancelSigmet(selectedAviationProduct)
                  ? selectedAviationProduct
                  : null!
              }
              initialSigmet={
                selectedAviationProduct !== undefined &&
                !isInstanceOfCancelSigmet(selectedAviationProduct)
                  ? (selectedAviationProduct as Sigmet)
                  : null!
              }
              isCancelSigmet={
                selectedAviationProduct !== undefined &&
                isInstanceOfCancelSigmet(selectedAviationProduct)
              }
              lockValidFrom={lockValidFrom}
              mode={mode!}
              product={selectedAviationProduct}
              productConfig={aviationConfig as SigmetConfig}
              showMap={false}
              storedEndGeoJSON={storedEndGeoJSON}
              storedGeoJSON={storedStartGeoJSON}
            />
          </Box>
        );
      }
      return (
        <Box
          sx={{
            maxWidth: { xs: '550px', sm: '750px' },
            marginLeft: '-80px',
          }}
        >
          <AirmetForm
            getTAC={async (product) => fetchAirmetTAC(product)}
            initialAirmet={
              selectedAviationProduct !== undefined &&
              !isInstanceOfCancelAirmet(selectedAviationProduct)
                ? (selectedAviationProduct as Airmet)
                : null!
            }
            initialCancelAirmet={
              selectedAviationProduct !== undefined &&
              isInstanceOfCancelAirmet(selectedAviationProduct)
                ? selectedAviationProduct
                : null!
            }
            isCancelAirmet={
              selectedAviationProduct !== undefined &&
              isInstanceOfCancelAirmet(selectedAviationProduct)
            }
            lockValidFrom={lockValidFrom}
            mode={mode!}
            product={selectedAviationProduct}
            productConfig={aviationConfig as AirmetConfig}
            showMap={false}
            storedGeoJSON={storedStartGeoJSON}
          />
        </Box>
      );
    }
    if (warningType === 'maritimeWarningCoastal') {
      return (
        <MaritimeWarningsFormConnect
          domain="coastalWaters"
          onModifyForm={onFormDirty}
        />
      );
    }
    if (warningType === 'maritimeWarningNorthSea') {
      return (
        <MaritimeWarningsFormConnect
          domain="northSeaDistricts"
          onModifyForm={onFormDirty}
        />
      );
    }
    // Public warnings form
    return (
      <ContainerGrid>
        <Grid columnSpacing={2} container rowSpacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }}>
            <Phenomenon
              isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
              isReadOnly={isReadOnly}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <AreaField
              drawActions={
                <>
                  <CustomTooltip title={drawToolsDisabledTooltip}>
                    <span data-testid="warning-draw-polygon-tooltip-anchor">
                      <Button
                        data-testid="warning-draw-polygon"
                        disabled={isDrawPolygonDisabled}
                        onClick={onDrawPolygon}
                        size="small"
                        startIcon={<DrawPolygon />}
                        variant={isDrawPolygonActive ? 'primary' : 'tertiary'}
                      >
                        {t('warning-button-draw-polygon')}
                      </Button>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip title={deletePolygonDisabledTooltip}>
                    <span data-testid="warning-delete-polygon-tooltip-anchor">
                      <Button
                        data-testid="warning-delete-polygon"
                        disabled={isDeletePolygonDisabled}
                        onClick={onDeletePolygon}
                        size="small"
                        startIcon={<Delete />}
                        variant="tertiary"
                      >
                        {t('warning-button-delete-polygon')}
                      </Button>
                    </span>
                  </CustomTooltip>
                </>
              }
              isDisabled={
                areFieldsDisabled || isReadOnly || isPublicWarningInDrawingMode
              }
              onAddObject={onAddObject}
              onDeleteObject={(): void => {}}
              onEditObject={(): void => {}}
              onViewObject={(): void => {}}
            />
            {!!isPublicWarningInDrawingMode && (
              <FormHelperText sx={{ marginTop: 1 }} variant="filled">
                {t('drawing-tool-form-exit-draw-mode')}
              </FormHelperText>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <WarningsMapView
              defaultMapPreset={defaultMapPreset}
              geojsonFeature={warningGeoJSONFeature}
              isSnapshot={isSnapshot}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <ValidFrom
              isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
              isReadOnly={isReadOnly}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ValidUntil
              isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
              isReadOnly={isReadOnly}
            />
          </Grid>

          <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LevelField
                isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
                isReadOnly={isReadOnly}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ProbabilityField
                isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
                isReadOnly={isReadOnly}
              />
            </Grid>
          </Grid>

          <Grid container size={{ xs: 12 }} spacing={2}>
            <Grid size={{ xs: 12 }}>
              <DescriptionField
                isDisabled={areFieldsDisabled || isPublicWarningInDrawingMode}
                isReadOnly={isReadOnly}
              />
            </Grid>
          </Grid>
        </Grid>
      </ContainerGrid>
    );
  };

  if (isMaritimeWarning) {
    return getFormContent();
  }

  return (
    <ContainerWrapper>
      <ContainerFormWrapper
        data-testid="scroll-container"
        isReadOnly={isReadOnly}
        ref={ref}
      >
        {!!errors && (
          <Box sx={{ marginBottom: 2 }}>
            <AlertBanner
              info={errors.errors as unknown as React.ReactElement}
              severity={error?.severity}
              shouldClose
              title={errors.title}
            />
          </Box>
        )}
        {getFormContent()}
        <DraftFieldHelper />
        <ReactHookFormHiddenInput name="id" />
      </ContainerFormWrapper>
      {formState !== 'cancel' && !isReadOnly && (
        <Box
          sx={{
            padding: '24px 10px 14px 10px',
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'geowebColors.background.surfaceApp',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
            zIndex: 1,
            overflowX: 'hidden',
            border: 'none',
          }}
        >
          <ContainerGrid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  disabled={areFieldsDisabled || isPublicWarningInDrawingMode}
                  onClick={onPressClear}
                  sx={{
                    width: '100%',
                    marginBottom: 1,
                  }}
                  variant="tertiary"
                >
                  {t('warning-button-clear')}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  disabled={
                    isPublishing ||
                    formState === 'amending' ||
                    isPublicWarningInDrawingMode
                  }
                  onClick={async () =>
                    selectedAviationProduct
                      ? handleSubmitAviationProduct(
                          ProductAction.SAVE,
                          warningType as 'sigmet' | 'airmet',
                        )
                      : onPressSave()
                  }
                  sx={{
                    width: '100%',
                    marginBottom: 1,
                  }}
                  variant="tertiary"
                >
                  <span style={{ position: 'relative' }}>
                    {isSaving
                      ? t('warning-button-saving')
                      : t('warning-button-save')}
                    {!!isSaving && (
                      <CircularProgress
                        color="inherit"
                        data-testid="spinner"
                        sx={spinnerSx}
                      />
                    )}
                  </span>
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  disabled={isSaving || isPublicWarningInDrawingMode}
                  onClick={() => {
                    void (selectedAviationProduct
                      ? handleSubmitAviationProduct(
                          formState === 'amending'
                            ? ProductAction.AMEND
                            : ProductAction.PUBLISH,
                          warningType as 'sigmet' | 'airmet',
                        )
                      : onPressPublish());
                  }}
                  sx={{
                    width: '100%',
                  }}
                  variant="primary"
                >
                  <span style={{ position: 'relative' }}>
                    {isPublishing
                      ? t('warning-button-publishing')
                      : t('warning-button-publish')}
                    {!!isPublishing && (
                      <CircularProgress
                        color="inherit"
                        data-testid="spinner"
                        sx={spinnerSx}
                      />
                    )}
                  </span>
                </Button>
              </Grid>
            </Grid>
          </ContainerGrid>
        </Box>
      )}
      {formState === 'cancel' && (
        <Box
          sx={{
            padding: '24px 10px 14px 10px',
            position: 'absolute',
            bottom: 0,
            width: '100%',
            backgroundColor: 'geowebColors.background.surfaceApp',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
            zIndex: 1,
            overflowX: 'hidden',
            border: 'none',
          }}
        >
          <ContainerGrid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <Button
                  disabled={isPublishing}
                  onClick={() => {
                    void (selectedAviationProduct
                      ? handleSubmitAviationProduct(
                          ProductAction.CANCEL,
                          warningType as 'sigmet' | 'airmet',
                        )
                      : onPressPublish());
                  }}
                  sx={{ width: '100%' }}
                  variant="primary"
                >
                  <span style={{ position: 'relative' }}>
                    {getPublishButtonLabel()}
                    {!!isPublishing && (
                      <CircularProgress
                        color="inherit"
                        data-testid="spinner"
                        sx={spinnerSx}
                      />
                    )}
                  </span>
                </Button>
              </Grid>
            </Grid>
          </ContainerGrid>
        </Box>
      )}
    </ContainerWrapper>
  );
};

export const PublicWarningsForm: React.FC<FormProps> = (props) => {
  const { object, publicWarningDetails, selectedAviationProduct } = props;

  const defaultValues =
    selectedAviationProduct || getFormData(publicWarningDetails, object);

  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        mode: selectedAviationProduct ? defaultFormOptions.mode : 'onSubmit',
        defaultValues,
      }}
    >
      <Form {...props} />
    </ReactHookFormProvider>
  );
};
